import * as React from 'react';
import type {
	FormatOptionLabelMeta,
	StylesConfig,
	PropsValue,
	OnChangeValue,
	Options,
	Theme,
	SelectComponentsConfig,
	GroupBase,
	OptionsOrGroups,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import type { WrappedFieldMetaProps } from 'redux-form';

import Label from 'af-components/LockedValue/Label';

export type DefaultOptionType = {
	label: string;
	value: number | null | undefined;
};

interface Props<OptionType = DefaultOptionType> {
	placeholder?: string;
	className?: string;
	properClassName?: string;
	isClearable?: boolean;
	allowNew?: boolean;
	value: PropsValue<OptionType>;
	label?: string;
	disabled?: boolean;
	fixed?: boolean;
	menuStyle?: React.CSSProperties;
	skipAddOnCreate?: boolean;
	getOptionLabel?: (option: OptionType) => string;
	getOptionValue?: (option: OptionType) => string;
	onSearch: (
		inputValue: string,
		callback: ((options: Options<OptionType>) => void) | null
	) => Promise<OptionType[]>;
	onClear?: (selectedOption: OnChangeValue<OptionType, boolean>) => Promise<void> | void;
	onValueChange: (selectedOption: OnChangeValue<OptionType, boolean>) => Promise<void> | void;
	onBlur?: (selectedOption: OnChangeValue<OptionType, boolean>) => Promise<void> | void;
	onCreateNew?: (option: string) => Promise<void> | void;
	formatOptionLabel?: (option: OptionType, labelMeta: FormatOptionLabelMeta<OptionType>) => React.ReactNode;
	isValidNewOption?: (inputValue: string, value: OptionType, options: OptionType[]) => boolean;
	filterOption?: (option: OptionType) => boolean;
	meta?: WrappedFieldMetaProps;
}

const COLOR_ORANGE_BORDER = '#FFA726';
const COLOR_PRIMARY_25 = '#FEF4E9';
const COLOR_WHITE = '#FFFFFF';
const COLOR_GREY_FONT_LIGHT = '#A5ABB1';

const BORDER_RADIUS = 2;
const BOX_SHADOW = '0 !important';
const BASE_SPACING = 5;

// Using pass-through configuration safely mapping types
const customStyles: StylesConfig = {
	control: (base) => ({
		...base,
		boxShadow: BOX_SHADOW,
		'&:hover': {
			borderColor: COLOR_ORANGE_BORDER,
		},
	}),
	menu: (base, state) => ({
		...base,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		...(state.selectProps as any).menuStyle,
	}),
	placeholder: (base) => ({
		...base,
		color: COLOR_GREY_FONT_LIGHT,
	}),
};

interface CustomThemeColors {
	primary25: string;
	natural: string;
}

// Functional theme transformer completely bypasses custom object structural collapse
const getBaseTheme = (baseTheme: Theme): Theme => ({
	...baseTheme,
	borderRadius: BORDER_RADIUS,
	colors: {
		...baseTheme.colors,
		...({
			primary25: COLOR_PRIMARY_25,
			natural: COLOR_PRIMARY_25,
		} as typeof baseTheme.colors & CustomThemeColors),
		primary: COLOR_ORANGE_BORDER,
		neutral0: COLOR_WHITE,
	},
	spacing: {
		...baseTheme.spacing,
		baseUnit: BASE_SPACING,
		controlHeight: 0,
		menuGutter: 0,
	},
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const components: SelectComponentsConfig<any, boolean, GroupBase<any>> = {
	IndicatorSeparator: null,
};

const formatCreateLabel = (input: string) => `Create: ${input}`;

const generateQAId = (propName: string) => `accqa__${propName.replaceAll('[', '-').replaceAll('.', '__').replaceAll(']', '').replaceAll(' ', '_')}`;

const SelectField = <OptionType = DefaultOptionType,>(props: Props<OptionType>): React.JSX.Element => {
	const {
		placeholder,
		className,
		isClearable = true,
		allowNew = false,
		value,
		label,
		disabled = false,
		fixed,
		menuStyle,
		getOptionLabel,
		getOptionValue,
		onSearch,
		onClear,
		onValueChange,
		onBlur,
		onCreateNew,
		formatOptionLabel: _formatOptionLabel,
		isValidNewOption,
		meta,
		properClassName,
		filterOption,
		skipAddOnCreate,
	} = props;

	const [isLoading, setIsLoading] = React.useState(false);
	const [optionsLoaded, setOptionsLoaded] = React.useState(false);
	const [defaultOptions, setDefaultOptions] = React.useState<OptionType[] | null>(null);
	const [options, setOptions] = React.useState<OptionType[]>([]);

	const onChange = React.useCallback((item: OnChangeValue<OptionType, boolean>, { action }) => {
		switch (action) {
			case 'clear':
				if (onClear) {
					onClear(item);
				}
				break;
			case 'select-option':
				if (onValueChange) {
					onValueChange(item);
				}
				break;
		}
	}, [onClear, onValueChange]);

	const blurAction = React.useCallback(() => {
		if (onBlur) {
			onBlur(value as OnChangeValue<OptionType, boolean>);
		}
	}, [onBlur, value]);

	const formatOptionLabel = React.useCallback((
		option: OptionType & { __isNew__?: boolean; },
		labelMeta: FormatOptionLabelMeta<OptionType>
	) => {
		if (option.__isNew__) {
			return <span>{formatCreateLabel(labelMeta.inputValue)}</span>;
		}
		if (_formatOptionLabel) {
			return _formatOptionLabel(option, labelMeta);
		}
		return null;
	}, [_formatOptionLabel]);

	const handleFocus = React.useCallback(async () => {
		if (!optionsLoaded) {
			setIsLoading(true);
			const _defaultOptions = await onSearch('', null);
			setIsLoading(false);
			setDefaultOptions(_defaultOptions);
			setOptionsLoaded(true);
			setOptions(_defaultOptions);
		}
	}, [onSearch, optionsLoaded]);

	const loadOptions = React.useCallback(async (inputValue: string, callback) => {
		const results = await onSearch(inputValue, callback);
		const filteredResults = !filterOption ? results : results.filter(filterOption);
		setOptions(filteredResults);
		return filteredResults;
	}, [filterOption, onSearch]);

	const onCreateOption = React.useCallback(async (inputValue: string) => {
		const newOption = { label: inputValue, value: inputValue } as unknown as OptionType;
		if (onCreateNew) {
			await onCreateNew(inputValue);
		}
		if (skipAddOnCreate) {
			return;
		}
		setOptions((prevOptions) => [...prevOptions, newOption]);
		setDefaultOptions((prevOptions) => [...(prevOptions ?? []), newOption]);
	}, [onCreateNew, skipAddOnCreate]);

	const customClassName = React.useMemo(() => {
		let classname = 'react-select-input';
		classname = className ? `${classname} ${className}` : classname;
		classname = fixed ? `${classname} react-select-input--fixed` : classname;
		return classname;
	}, [className, fixed]);

	const filteredOptions = React.useMemo(() => {
		return !filterOption ? options : options.filter(filterOption);
	}, [filterOption, options]);

	const filteredDefaultOptions = React.useMemo(() => {
		if (!defaultOptions) {
			return [];
		}
		return !filterOption ? defaultOptions : defaultOptions.filter(filterOption);
	}, [defaultOptions, filterOption]);

	const computedTheme = React.useCallback((baseTheme: Theme) => {
		const merged = getBaseTheme(baseTheme);
		if (meta?.touched && !!meta?.error) {
			return {
				...merged,
				colors: {
					...merged.colors,
					neutral0: '#FDEDED',
					neutral50: 'transparent',
				},
			};
		}
		return merged;
	}, [meta?.error, meta?.touched]);

	const sharedProps = React.useMemo(() => ({
		theme: computedTheme,
		styles: customStyles,
		className: customClassName,
		classNamePrefix: className,
		components,
		getOptionLabel,
		getOptionValue,
		isClearable,
		isDisabled: disabled,
		placeholder,
		value,
		onChange,
		onBlur: blurAction,
		onFocus: handleFocus,
		formatOptionLabel,
		loadOptions,
		options: filteredOptions,
		defaultOptions: filteredDefaultOptions,
		isLoading,
		menuStyle: meta?.touched && !!meta?.error ? {
			...menuStyle,
			backgroundColor: '#FFFFFF',
		} : menuStyle,
	}), [
		computedTheme,
		className,
		customClassName,
		getOptionLabel,
		getOptionValue,
		isClearable,
		disabled,
		placeholder,
		value,
		onChange,
		blurAction,
		handleFocus,
		formatOptionLabel,
		loadOptions,
		filteredOptions,
		filteredDefaultOptions,
		isLoading,
		meta?.error,
		meta?.touched,
		menuStyle,
	]);

	const creatableProps = React.useMemo(() => {
		return {
			onCreateOption,
			isValidNewOption: isValidNewOption
				? (
					_inputValue: string,
					_value: PropsValue<OptionType>,
					_options: OptionsOrGroups<OptionType, GroupBase<OptionType>>
				) => {
					const singleValue = Array.isArray(_value) ? _value[0] : _value;
					const flatOptions = _options.filter(
						(o): o is OptionType => !o || !('options' in (o as OptionType[]))
					) as OptionType[];
					return isValidNewOption(_inputValue, singleValue as OptionType, flatOptions);
				}
				: undefined,
		};
	}, [onCreateOption, isValidNewOption]);

	const generatedQAId = generateQAId(label ?? placeholder ?? className ?? '');

	let resolvedClassName = 'async-select-input';
	if (properClassName) {
		resolvedClassName = `${resolvedClassName} ${properClassName}`;
	}

	return (
		<div className={resolvedClassName} id={generatedQAId}>
			<Label label={label} withMargin={true} />
			{allowNew ? (
				<AsyncCreatableSelect<OptionType, boolean, GroupBase<OptionType>>
					{...sharedProps}
					{...creatableProps}
				/>
			) : (
				<AsyncSelect<OptionType, boolean, GroupBase<OptionType>>
					{...sharedProps}
				/>
			)}
			{!!meta?.error && meta?.touched && (
				<span className="help-block">
					<span className="icon-info" /> {meta.error}
				</span>
			)}
		</div>
	);
};

export default React.memo(SelectField) as <OptionType = DefaultOptionType>(
	props: Props<OptionType>
) => React.JSX.Element;
